import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import toast from "react-hot-toast";
import { updateWorkItem } from "@/Redux/Thunks/projectManagementThunk";
import { fetchProjectStatuses } from "@/Redux/Thunks/projectStatusesThunk";
import { selectProjectStatusesResponse } from "@/Redux/Slices/projectStatusesSlice";
import {
  selectKanbanBoard,
  selectKanbanBoardColumnDefs,
} from "../../Redux/Slices/kanbanColumnsSlice";
import {
  fetchKanbanBoardBootstrap,
  fetchKanbanColumnPage,
} from "../../Redux/Thunks/kanbanColumnsThunk";
import {
  applyGlobalKanbanOnHoldColumn,
  buildGlobalKanbanColumns,
  findIssueInKanbanColumnData,
  resolveProjectStatusIdForCategory,
} from "./issueMappers";
import { getWorkItemStatusCategoryLabel } from "@/constants/workItemStatusCategoryTheme";
import KanbanBoard from "@/Pages/Projects/project-detail/boards/KanbanBoard";
import { KANBAN_DEFAULT_LIMIT } from "../../constants";

export const GLOBAL_BOARD_KEY = "global";

const FALLBACK_COLUMNS_OFF_HOLD = buildGlobalKanbanColumns({ includeOnHold: false });

/**
 * Global Issues Kanban. Reads per-column state from kanbanColumnsSlice.
 * Column definitions come from GET /work-items/kanban/initial (`boardColumnDefs`).
 * Initial fetches are dispatched by Issues.jsx when the kanban tab is active.
 *
 * Drag-and-drop: columns are status *categories* (NEW, IN_PROGRESS, …). Each project
 * maps categories to its own workflow status UUIDs — resolved on drop via that item's projectId.
 */
const IssueKanban = ({
  onOpenWorkItemDetail,
  assigneeFilterIds,
  onAssigneeFilterIdsChange,
  /** Full global listing scope (saved filters, quick filter, search) for Kanban GET query params. */
  kanbanListingFilter,
  showOnHold = false,
  onShowOnHoldChange,
  /** Kanban board's own search box — server-driven, owned by Issues.jsx (part of kanbanListingFilter.search). */
  searchTerm,
  onSearchTermChange,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [statusPatchPending, setStatusPatchPending] = useState(false);
  const boardState = useSelector((state) => selectKanbanBoard(state, GLOBAL_BOARD_KEY));
  const columnDefsFromStore = useSelector((state) =>
    selectKanbanBoardColumnDefs(state, GLOBAL_BOARD_KEY)
  );
  const boardColumns = useMemo(() => {
    const base = columnDefsFromStore.length ? columnDefsFromStore : FALLBACK_COLUMNS_OFF_HOLD;
    return applyGlobalKanbanOnHoldColumn(base, showOnHold);
  }, [columnDefsFromStore, showOnHold]);

  // Before the bootstrap fires, boardState is null — provide all columns in
  // initialLoading:true so the loader shows immediately instead of flashing empty.
  const columnData = useMemo(() => {
    if (boardState) return boardState;
    const loading = {};
    boardColumns.forEach((col) => {
      loading[col.key] = {
        items: [],
        nextCursor: null,
        hasMore: false,
        initialLoading: true,
        loadingMore: false,
        error: null,
      };
    });
    return loading;
  }, [boardState, boardColumns]);

  const handleRefetch = useCallback(() => {
    dispatch(
      fetchKanbanBoardBootstrap({
        boardKey: GLOBAL_BOARD_KEY,
        listingFilter: kanbanListingFilter,
        limit: KANBAN_DEFAULT_LIMIT,
        columns: boardColumns,
      })
    );
  }, [dispatch, kanbanListingFilter, boardColumns]);

  const handleStatusChange = useCallback(
    async (issueId, payload) => {
      const category = payload?.category;
      if (!issueId || !category) return;

      const issue = findIssueInKanbanColumnData(columnData, issueId);
      const projectId = issue?.projectId;
      if (!projectId) {
        toast.error("Cannot update status: work item has no project.");
        return;
      }

      const version = issue?.version ?? 1;
      setStatusPatchPending(true);
      try {
        let statusesResponse = selectProjectStatusesResponse(store.getState(), projectId);

        const statusList = statusesResponse?.data;
        if (!Array.isArray(statusList) || statusList.length === 0) {
          statusesResponse = await dispatch(
            fetchProjectStatuses({ projectId, context: "global-kanban-dnd" })
          ).unwrap();
        }

        const statusId = resolveProjectStatusIdForCategory(statusesResponse, category);
        if (!statusId) {
          const label = getWorkItemStatusCategoryLabel(category);
          toast.error(
            `No workflow status configured for "${label}" in this project. Add one in project settings.`
          );
          return;
        }

        await dispatch(
          updateWorkItem({
            projectId: String(projectId),
            workItemId: String(issueId),
            body: { version, statusId },
          })
        ).unwrap();

        handleRefetch();
      } catch {
        /* ApiBase toast */
      } finally {
        setStatusPatchPending(false);
      }
    },
    [columnData, dispatch, handleRefetch, store]
  );

  const handleLoadMore = useCallback(
    (columnKey) => {
      const colState = boardState?.[columnKey];
      if (!colState || colState.loadingMore || !colState.hasMore) return;
      const colDef = boardColumns.find((c) => c.key === columnKey);
      const statusCategories = colDef?.category;
      if (!statusCategories) return;
      dispatch(
        fetchKanbanColumnPage({
          boardKey: GLOBAL_BOARD_KEY,
          columnKey,
          isLoadMore: true,
          statusCategories,
          listingFilter: kanbanListingFilter,
          page: (colState.page ?? 1) + 1,
          limit: KANBAN_DEFAULT_LIMIT,
        })
      );
    },
    [dispatch, boardState, boardColumns, kanbanListingFilter]
  );

  return (
    <div className="mt-4 w-full max-w-full min-w-0">
      <KanbanBoard
        issues={[]}
        onStatusChange={handleStatusChange}
        projectSlug={undefined}
        statusPatchPending={statusPatchPending}
        boardColumns={boardColumns}
        assigneeFilterIds={assigneeFilterIds}
        onAssigneeFilterIdsChange={onAssigneeFilterIdsChange}
        onOpenWorkItemDetail={onOpenWorkItemDetail}
        onRefetchProjectKanbanIssues={handleRefetch}
        columnData={columnData}
        onLoadMore={handleLoadMore}
        showOnHold={showOnHold}
        onShowOnHoldChange={onShowOnHoldChange}
        searchTerm={searchTerm}
        onSearchTermChange={onSearchTermChange}
      />
    </div>
  );
};

export default IssueKanban;
